import { useCallback } from "react";

/**
 * Хук useTableControls
 *
 * Назначение:
 * Управляет взаимодействием с таблицей и её представлениями: смена вида отображения,
 * сортировка, пагинация. Инкапсулирует логику обновления параметров таблицы и синхронизации
 * состояния с хранилищем и сервером.
 *
 * Что делает:
 * 1. handleViewChange - переключает вид отображения (список, карточки, канбан)
 * 2. handleSortChange - изменяет порядок сортировки и сбрасывает на первую страницу
 * 3. handlePaginationChange - обновляет номер страницы и размер страницы
 *
 * Поведение:
 * - При смене сортировки автоматически сбрасывает пользователя на страницу 1
 * - Все обработчики мемоизированы (useCallback) для оптимизации перерисовок
 * - После каждого изменения вызывает setParams (сохранение в localStorage) и fetchData
 *
 * Используется в:
 * - Основном компоненте страницы Tasks для управления таблицей и видами
 *
 * @param {Object} tableParams - текущие параметры таблицы (select_view, sort, pagination, filters)
 * @param {Function} setParams - функция для сохранения параметров (с записью в localStorage)
 * @param {Function} fetchData - функция для загрузки данных с сервера с новыми параметрами
 *
 * @returns {Object} Объект с четырьмя обработчиками для управления таблицей
 */
export const useTableControls = ({
  tableParams,
  setParams,
  fetchData,
  setData,
}) => {
  const handleViewChange = useCallback(
    (value) => {
      // Сбрасывает данные перед сменой вида, чтобы избежать мерцания старых данных
      setData([]);
      // Сохраняет выбранный вид отображения (список/карточки/канбан)
      setParams({ ...tableParams, select_view: value });
      fetchData({ ...tableParams, select_view: value });
    },
    [tableParams, setParams, fetchData, setData]
  );

  const handleSortChange = useCallback(
    (val) => {
      // Обновляет порядок сортировки и сбрасывает пагинацию на первую страницу
      // Это предотвращает ситуацию, когда пользователь остается на странице 5,
      // но после сортировки её может не существовать
      const updatedTableParams = {
        ...tableParams,
        sort: val,
        pagination: { ...tableParams.pagination, current: 1 },
      };
      setParams(updatedTableParams);
      fetchData(updatedTableParams);
    },
    [tableParams, setParams, fetchData]
  );

  const handlePaginationChange = useCallback(
    (current, pageSize) => {
      // Обработчик для программного изменения пагинации
      // Используется когда нужно явно задать номер страницы и размер
      // (например, при фильтрации или поиске)

      // При изменении pageSize сбрасываем страницу на 1
      const shouldResetPage = pageSize !== tableParams.pagination.pageSize;

      const updatedTableParams = {
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          current: shouldResetPage ? 1 : current,
          pageSize,
        },
      };
      setParams(updatedTableParams);
      fetchData(updatedTableParams);
    },
    [tableParams, setParams, fetchData]
  );

  return {
    handleViewChange,
    handleSortChange,
    handlePaginationChange,
  };
};
